import React, { createContext, useContext, useState } from 'react';
import { Translations, arabicTranslations, englishTranslations } from '../translations';

type UILanguage = 'english' | 'arabic';

interface LanguageContextType {
  t: Translations;
  isRTL: boolean;
  uiLanguage: UILanguage;
  setUILanguage: (language: UILanguage) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

const getTranslations = (uiLanguage: UILanguage): Translations => {
  switch (uiLanguage) {
    case 'arabic':
      return arabicTranslations;
    case 'english':
    default:
      return englishTranslations;
  }
};

const isRTLLanguage = (uiLanguage: UILanguage): boolean => {
  return uiLanguage === 'arabic';
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [uiLanguage, setUILanguageState] = useState<UILanguage>(() => {
    // Check for saved UI language preference or default to English
    const savedUILanguage = localStorage.getItem('uiLanguage') as UILanguage;
    return savedUILanguage || 'english';
  });
  
  const setUILanguage = (language: UILanguage) => {
    setUILanguageState(language);
    localStorage.setItem('uiLanguage', language);
  };

  const translations = getTranslations(uiLanguage);
  const isRTL = isRTLLanguage(uiLanguage);

  return (
    <LanguageContext.Provider value={{ 
      t: translations, 
      isRTL, 
      uiLanguage,
      setUILanguage
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
